import { Component, Prop, Element, h } from '@stencil/core';

const COMPONENT_NAME = 'stats-type-6';

@Component({
  tag: 'stats-type-6',
  styleUrl: 'index.scss',
  shadow: false,
})
export class StatsType6 {
  @Element() el: HTMLElement;

  /**
   * @type {array}
   * @counter.type {object}
   * @counter.property {string} value - Main value to display inside the counter (e.g., "500+", "42%", "$150", etc.). Max length: 4.
   * @counter.property {string} caption - Caption or label shown below or above the value (up to 80 characters, will be truncated if exceeds 2 lines).
   * @counter.property {string} description - Description for counter (up to 120 characters, will be truncated if exceeds 3 lines).
   * @counter.required value
   * @counter.required caption
   * @counter.required description
   * @minItems 1
   * @maxItems 3
   */
  @Prop() counters!: any[];

  /**
   * Type of counter appearance animation
   * @default 'none'
   */
  @Prop({ reflect: true }) animationType: 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right' | 'none' = 'none';

  /**
   * Animation mode (simultaneous, sequential, etc.)
   * @default 'staggered'
   */
  @Prop({ reflect: true }) animationMode: 'all-at-once' | 'sequential' | 'staggered' = 'staggered';

  /**
   * Animation duration in milliseconds
   * @default 600
   */
  @Prop({ reflect: true }) animationDuration: number = 600;

  /**
   * Counter animation duration in milliseconds
   * @default 1500
   */
  @Prop({ reflect: true }) counterAnimationDuration: number = 1500;

  /**
   * Animation easing function
   * @default 'ease'
   */
  @Prop({ reflect: true }) animationEasing: 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear' = 'ease';

  /**
   * Delay between elements for sequential/staggered modes (in ms)
   * @default 200
   */
  @Prop({ reflect: true }) animationDelay: number = 200;

  /**
   * Horizontal content alignment inside the stats counter card: 'left', 'center', or 'right'
   * @default 'right'
   */
  @Prop() align: 'left' | 'center' | 'right' = 'right';

  /**
   * Background color theme. Use 'white' for light backgrounds and 'dark' for dark backgrounds.
   * @type {string}
   * @default 'white'
   * {enum}: ['white', 'dark']
   */
  @Prop() theme: 'white' | 'dark' = 'white';

  private observer: IntersectionObserver;

  componentDidLoad() {
    const countersContainer = this.el.querySelector(`.${COMPONENT_NAME}__counters`);
    if (!countersContainer) return;

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cardElements = entry.target.querySelectorAll(`.${COMPONENT_NAME}__counter`);
            this.animateCounters(cardElements);
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    this.observer.observe(countersContainer);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private animateCounters(cardElements: NodeListOf<Element>) {
    if (this.animationType == 'none') {
      cardElements.forEach(el => {
        el.classList.add('is-visible');
      });
      return;
    }

    let delay = 0;

    switch (this.animationMode) {
      case 'staggered':
        delay = this.animationDelay;
        break;
      case 'sequential':
        delay = this.animationDuration + this.animationDelay;
        break;
      case 'all-at-once':
      default:
        delay = 0;
        break;
    }
    cardElements.forEach((el, index) => {
      setTimeout(() => {
        el.classList.add('is-visible');
      }, index * delay);
    });
  }

  render() {
    return (
      <me-section class={`${COMPONENT_NAME} ${COMPONENT_NAME}--${this.theme}`} yGap="2xl" yGapLg="5xl" role="region" aria-label="Statistics">
        <me-row>
          <me-column>
            <div class={`${COMPONENT_NAME}__counters`} style={{ '--animation-duration': `${this.animationDuration}ms` }} itemScope itemType="https://schema.org/ItemList">
              {this.counters.map((counter, index) => (
                <stats-counter-card-type-3
                  value={counter.value}
                  caption={counter.caption}
                  description={counter.description}
                  speed={this.counterAnimationDuration}
                  key={index}
                  class={`${COMPONENT_NAME}__counter`}
                  align={this.align}
                  theme={this.theme}
                  {...({
                    itemScope: true,
                    itemType: 'https://schema.org/ListItem',
                    itemProp: 'itemListElement',
                    position: String(index + 1),
                  } as any)}
                />
              ))}
            </div>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
